import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

// eslint-disable-next-line @conarti/feature-sliced/layers-slices -- getCexActualBalance is a shared API function
import { getCexActualBalance } from 'entities/cex-statistic';
import { IAccountBalances, ICexAccount } from 'types/accounts';
import { ECexName } from 'web3';

import { useGetAccounts } from './useGetAccounts';

import { CEX_ACCOUNT_QUERY_KEYS } from '../api.const';

export interface IAccountWithBalance extends ICexAccount, Partial<IAccountBalances> {}

interface UseAccountsWithBalancesOptions {
  pairId: number | undefined;
  cex: ECexName | undefined;
  symbol: string;
  enabled?: boolean;
}

const EMPTY_BALANCES: IAccountBalances = {
  base_balance: '0',
  base_balance_locked: '0',
  quote_balance: '0',
  quote_balance_locked: '0',
};

/**
 * Combined hook for fetching CEX accounts with their balances.
 * Replaces the legacy CexPairContext accounts data.
 *
 * @param options - Configuration options
 * @param options.pairId - CEX pair ID
 * @param options.cex - Exchange name
 * @param options.symbol - Trading pair symbol (e.g., 'BTC-USDT')
 * @param options.enabled - Whether to enable the query
 * @returns Accounts with balances, loading states, and control functions
 *
 * @example
 * ```ts
 * const { accounts, isLoading, isFetching, refetch, setAccounts } = useAccountsWithBalances({
 *   pairId: cexPair?.id,
 *   cex: cexPair?.cex,
 *   symbol: `${cexPair?.token_base.symbol}-${cexPair?.token_quote.symbol}`,
 *   enabled: Boolean(cexPair),
 * });
 * ```
 */
export function useAccountsWithBalances({
  pairId,
  cex,
  symbol,
  enabled = true,
}: UseAccountsWithBalancesOptions) {
  const queryClient = useQueryClient();

  const isQueryEnabled = enabled && pairId != null && Boolean(cex) && Boolean(symbol);

  const accountsQuery = useGetAccounts(
    { pairId: pairId ?? 0, limit: 1000 },
    { enabled: isQueryEnabled },
  );

  const accountItems = useMemo(
    () => accountsQuery.data?.data?.items ?? [],
    [accountsQuery.data?.data?.items],
  );

  // Fetch balances for all accounts in parallel
  const balanceQueries = useQueries({
    queries: accountItems.map(account => ({
      queryKey: ['cex-account', 'balance', account.id, cex, symbol] as const,
      queryFn: async () => {
        if (!cex || !symbol) return null;
        const { data } = await getCexActualBalance({
          cex,
          symbol,
          account_id: [account.id],
        });
        return data ?? null;
      },
      enabled: accountsQuery.isSuccess && isQueryEnabled,
      staleTime: 60000,
    })),
  });

  // Merge accounts with their balances
  const accounts = useMemo<IAccountWithBalance[]>(() => {
    return accountItems.map((account, index) => {
      const balanceData = balanceQueries[index]?.data;
      return {
        ...account,
        ...(balanceData ?? EMPTY_BALANCES),
      };
    });
  }, [accountItems, balanceQueries]);

  // Set accounts in the query cache (replaces context setAccounts)
  const setAccounts = useCallback(
    (newAccounts: IAccountWithBalance[]) => {
      if (pairId == null) return;

      // Update the accounts query cache
      queryClient.setQueryData(
        CEX_ACCOUNT_QUERY_KEYS.accounts({ pairId, limit: 1000 }),
        (oldData: typeof accountsQuery.data) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              items: newAccounts,
            },
          };
        },
      );

      // Also update individual balance caches
      newAccounts.forEach(account => {
        const balanceData: IAccountBalances = {
          base_balance: account.base_balance ?? '0',
          base_balance_locked: account.base_balance_locked ?? '0',
          quote_balance: account.quote_balance ?? '0',
          quote_balance_locked: account.quote_balance_locked ?? '0',
        };
        queryClient.setQueryData(['cex-account', 'balance', account.id, cex, symbol], balanceData);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- typeof accountsQuery.data is type-only
    [queryClient, pairId, cex, symbol],
  );

  // Load balance for a specific account (replaces context loadAccountBalance)
  const loadAccountBalance = useCallback(
    async (accountId: number): Promise<IAccountWithBalance | undefined> => {
      if (!cex || !symbol) return undefined;

      try {
        const { data } = await getCexActualBalance({
          cex,
          symbol,
          account_id: [accountId],
        });

        if (data) {
          // Update the balance cache
          queryClient.setQueryData(['cex-account', 'balance', accountId, cex, symbol], data);

          // Find and return the updated account
          const account = accountItems.find(a => a.id === accountId);
          if (account) {
            return { ...account, ...data };
          }
        }

        return undefined;
      } catch (error) {
        console.error('Failed to load account balance:', error);
        return undefined;
      }
    },
    [cex, symbol, queryClient, accountItems],
  );

  // Refetch all data (replaces context refetchData/updateAccounts)
  const refetch = useCallback(async () => {
    await accountsQuery.refetch();
    // Balance queries will automatically refetch due to dependency on accountsQuery.isSuccess
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accountsQuery.refetch is stable
  }, [accountsQuery.refetch]);

  const isBalancesLoading = balanceQueries.some(q => q.isLoading);
  const isBalancesFetching = balanceQueries.some(q => q.isFetching);

  return {
    accounts,
    setAccounts,
    updateAccounts: refetch,
    loadAccountBalance,
    isLoading: accountsQuery.isLoading || isBalancesLoading,
    isFetching: accountsQuery.isFetching || isBalancesFetching,
    isInitialLoading: accountsQuery.isLoading,
    initialAccountsLoading:
      accountsQuery.isLoading || (accountItems.length > 0 && isBalancesLoading),
    accountsLoading: accountsQuery.isFetching || isBalancesFetching,
    refetch,
    refetchData: refetch,
  };
}
